// ** Third Party Components
import * as z from "zod"
import axios from "axios"
import { format } from "date-fns"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { CalendarDaysIcon, ChevronsUpDown, X } from "lucide-react"

// ** Internal Element
import { Input } from "@components/common/input"
import { Button } from "@components/common/button"
import { Heading } from "@components/common/heading"
import { Calendar } from "@components/common/calendar"
import { Textarea } from "@components/common/textarea"
import { Separator } from "@components/common/separator"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { Dialog, DialogContent, DialogDescription, DialogHeader } from "@components/common/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/common/form"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"

// ** Types
import { DictType } from "@pma/types"

// ** Api Routes
import { CREATE_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

// ** Store
import { useAuth } from "@stores/auth-store"
import { useModal } from "@stores/modal-store"

// ** Helpers
import { cn } from "@pma/utils/helpers"
import { getFilteredProjectsByCurrentUser } from "@pma/utils/utils"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Custom Components
import { UsersMultiSelect } from "../users/user-multi-select"

export const taskFormSchema = z
  .object({
    title: z.string().min(1, { message: "Enter project name" }),
    description: z.string().min(1, { message: "Enter a description" }).optional(),
    start_at: z.date().optional(),
    finish_to: z.date().optional(),
    project: z.object({ id: z.number(), title: z.string() }),
    responsible: z.object({ id: z.number() }).array().optional(),
  })
  .refine((data: DictType) => data?.start_at < data?.finish_to, {
    message: "End date cannot be before start date",
    path: ["finish_to"],
  })

export type ProjectFormValues = z.infer<typeof taskFormSchema>

const AddTaskModal = () => {
  const modal = useModal()
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const orgId = authConfig?.last_organization_id
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {},
    mode: "onChange",
  })

  const { data: projects } = useQuery({
    queryKey: ["projects", orgId],
    queryFn: () =>
      getFilteredProjectsByCurrentUser({
        by_organization_id: orgId,
        status_id: 0,
        paging_per_page: 30,
      }),
    enabled: modal.isOpen && modal.context === "task",
  })

  const addTask = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + CREATE_TASK_ENDPOINT,
      { ...payload },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  const addTaskMutation = useMutation({
    mutationKey: ["add-task"],
    mutationFn: addTask,
    onSuccess: (data) => {
      form.reset()
      newParams.set("revalidate", "tasks")
      navigate(`?${newParams.toString()}`)
      modal.onClose()
      modal.setContext(undefined)
      toast({
        title: translate("Success"),
        description: translate("Task added successfully"),
        variant: "success",
      })
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })

  const onSubmit = async (data: z.infer<typeof taskFormSchema>) => {
    const startDate = data?.start_at ? new Date(data?.start_at).getTime() : 0
    const endData = data?.finish_to ? new Date(data?.finish_to).getTime() : 0
    const assignedMember = data?.responsible?.map((obj) => obj.id) || []
    addTaskMutation.mutate({
      title: data?.title,
      description: data?.description,
      project_id: data?.project?.id,
      responsible_id: assignedMember,
      start_at: startDate,
      finish_to: endData,
    })
  }

  const handleClick = () => {
    modal.onClose()
    form.reset({})
    modal.setContext(undefined)
    return
  }

  return (
    <Dialog open={modal.isOpen && modal.context === "task"} onOpenChange={handleClick}>
      <DialogContent className='max-w-[700px]'>
        <DialogHeader>
          <Heading
            title={translate(`Create new task`)}
            description={translate("Fill the details to proceed")}
          />
        </DialogHeader>
        <DialogDescription className='grid gap-4'>
          <Separator />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className='container px-4 md:px-6'>
                <div className='mx-auto max-w-2xl'>
                  <div className='grid gap-6'>
                    <FormField
                      control={form.control}
                      name='title'
                      render={({ field }) => (
                        <FormItem className='capitalize'>
                          <FormLabel className='capitalize'>{translate("Title")}</FormLabel>
                          <FormControl>
                            <Input
                              placeholder={translate("Enter Title")}
                              className='text-black'
                              {...field}
                              disabled={addTaskMutation.isPending}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className='grid grid-cols-2 gap-6'>
                    <div className='mt-4'>
                      <FormField
                        control={form.control}
                        name={`project`}
                        render={({ field }) => (
                          <FormItem className='flex flex-col gap-1.5 capitalize'>
                            <FormLabel>{translate("Link to project")}</FormLabel>
                            <Popover>
                              <FormControl>
                                <PopoverTrigger
                                  disabled={addTaskMutation.isPending}
                                  className='flex items-center justify-between rounded-md border p-1.5'
                                >
                                  <div>
                                    {field?.value?.title ? (
                                      <div className='w-[23ch] overflow-hidden truncate text-ellipsis p-0.5 text-left text-xs font-semibold capitalize'>
                                        {field?.value?.title}
                                      </div>
                                    ) : (
                                      translate("Select Project")
                                    )}
                                  </div>
                                  <ChevronsUpDown size={12} />
                                </PopoverTrigger>
                              </FormControl>
                              <PopoverContent className='p-0'>
                                <Command>
                                  <CommandInput
                                    placeholder={translate("Search for project") + "..."}
                                  />
                                  <CommandList>
                                    <CommandEmpty>{translate("No projects found")}.</CommandEmpty>
                                    <CommandGroup>
                                      {projects?.items?.map((project: DictType) => (
                                        <CommandItem
                                          key={projects.id}
                                          value={String(project?.id)}
                                          defaultValue={project?.title}
                                          onSelect={() => {
                                            field.onChange({
                                              title: project?.title,
                                              id: project?.id,
                                            })
                                          }}
                                          className={cn(
                                            "my-1 capitalize",
                                            field?.value?.title === project.title &&
                                              "bg-secondary/30 text-black",
                                          )}
                                        >
                                          {project.title}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className='mt-4'>
                      <FormField
                        control={form.control}
                        name={`responsible`}
                        render={({ field }) => (
                          <FormItem className='capitalize'>
                            <FormLabel>{translate("Assign Users")}</FormLabel>
                            <UsersMultiSelect setUsers={field.onChange} />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className='grid grid-cols-2 gap-6'>
                    <div className='mt-4'>
                      <FormField
                        control={form.control}
                        name='start_at'
                        render={({ field }) => (
                          <FormItem className='flex-1'>
                            <FormLabel>{translate("Start Date")}</FormLabel>
                            <FormControl>
                              <Popover>
                                <div className='relative w-full'>
                                  <PopoverTrigger asChild>
                                    <Button
                                      className='w-full justify-start text-left font-normal'
                                      variant='outline'
                                    >
                                      <CalendarDaysIcon
                                        className={cn(
                                          "h-4 w-4",
                                          isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                        )}
                                      />
                                      {field.value ? (
                                        format(field.value, "PPP", {
                                          locale: isHebrew ? he : undefined,
                                        })
                                      ) : (
                                        <span>{translate("Select Date")}</span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  {field.value ? (
                                    <button
                                      type='button'
                                      onClick={(e) => {
                                        e.stopPropagation() // prevent Popover from opening
                                        form.setValue("start_at", undefined)
                                      }}
                                      className={cn(
                                        "absolute top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600",
                                        isHebrew ? "left-2" : "right-2",
                                      )}
                                    >
                                      <X size={14} />
                                    </button>
                                  ) : null}
                                </div>
                                <PopoverContent align='start' className='w-auto p-0'>
                                  <Calendar
                                    mode='single'
                                    selected={field?.value}
                                    onSelect={field.onChange}
                                    disabled={(date) =>
                                      date < new Date() || date < new Date("1900-01-01")
                                    }
                                    locale={isHebrew ? he : undefined}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className='mt-4'>
                      <FormField
                        control={form.control}
                        name='finish_to'
                        render={({ field }) => (
                          <FormItem className='flex-1'>
                            <FormLabel>{translate("Finish Date")}</FormLabel>
                            <FormControl>
                              <Popover>
                                <div className='relative w-full'>
                                  <PopoverTrigger asChild>
                                    <Button
                                      className='w-full justify-start text-left font-normal'
                                      variant='outline'
                                    >
                                      <CalendarDaysIcon
                                        className={cn(
                                          "h-4 w-4",
                                          isHebrew ? "ml-1 translate-x-1" : "mr-1 -translate-x-1",
                                        )}
                                      />
                                      {field.value ? (
                                        format(field.value, "PPP", {
                                          locale: isHebrew ? he : undefined,
                                        })
                                      ) : (
                                        <span>{translate("Select Date")}</span>
                                      )}
                                    </Button>
                                  </PopoverTrigger>
                                  {field.value ? (
                                    <button
                                      type='button'
                                      onClick={(e) => {
                                        e.stopPropagation() // prevent Popover from opening
                                        form.setValue("finish_to", undefined)
                                      }}
                                      className={cn(
                                        "absolute top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600",
                                        isHebrew ? "left-2" : "right-2",
                                      )}
                                    >
                                      <X size={14} />
                                    </button>
                                  ) : null}
                                </div>
                                <PopoverContent align='start' className='w-auto p-0'>
                                  <Calendar
                                    mode='single'
                                    selected={field?.value}
                                    onSelect={field.onChange}
                                    disabled={(date) =>
                                      date < new Date() || date < new Date("1900-01-01")
                                    }
                                    locale={isHebrew ? he : undefined}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                  <div className='grid gap-6'>
                    <div className='mt-4'>
                      <FormField
                        control={form.control}
                        name='description'
                        render={({ field }) => (
                          <FormItem className='space-y-1'>
                            <FormLabel>{translate("Description")}</FormLabel>
                            <FormControl>
                              <Textarea
                                className='min-h-[150px] flex-1 resize-none'
                                placeholder={translate(`Enter task description`)}
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <Button
                className='mt-4 w-full'
                type='submit'
                disabled={addTaskMutation.isPending}
                loading={addTaskMutation.isPending}
              >
                {translate("Save Task")}
              </Button>
            </form>
          </Form>
        </DialogDescription>
      </DialogContent>
    </Dialog>
  )
}

export default AddTaskModal
